import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type TColumns, useSortColumnsModal } from 'hooks/useSortColumnsModal';
import { getInlineString, getText } from 'i18n/intl';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { fetchFields, fieldsLoading, fieldsSelector } from 'slices/field.slice';
import {
  requestObjectTypes,
  selectObjectTypesList,
  selectObjectTypesLoading,
} from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  fetchPathwayConfigs,
  fetchingPathwayConfigsErrorSelector,
  isLoadingPathwayConfigsSelector,
  mappingChangesSelector,
  pathwayConfigsSelector,
  setMappingChanges,
} from 'slices/studyCombinations.slice';
import type { TType } from '@timeedit/types';
import { Button, EditableTable } from '@timeedit/ui-components';
import {
  Divider,
  Input,
  MenuProps,
  Space,
  Typography,
  theme,
} from '@timeedit/ui-components/antd';
import type {
  SelectInput,
  TreeSelectInput,
} from '@timeedit/ui-components/components/EditableTable/ColumnTitle';
import type { TMappingType, TMappings } from '.';
import { ErrorResult } from './ErrorResult';

const { useToken } = theme;

type TColumn = 'period' | TMappingType;

type TTableData = Record<'key' | TColumn, string>;

const SelectPopupComp = (props: {
  menu: React.ReactNode;
  fieldOptionsAdd: (valueToAdd: string) => void;
  changeValueOfSelectFromPopup: (valueToSetTo: string) => void;
}) => {
  const [name, setName] = useState('');

  const inputRef = useRef(null);
  const onNameChange = (event) => {
    setName(event.target.value);
  };
  const addItem = (e) => {
    e.preventDefault();
    props.fieldOptionsAdd(name);
    setName('');
    setTimeout(() => {
      let _a;
      (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, 0);
    props.changeValueOfSelectFromPopup(name);
  };

  return (
    <>
      <Space style={{ padding: '0 8px 4px' }}>
        <Input
          placeholder="Please enter item"
          ref={inputRef}
          value={name}
          onChange={onNameChange}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <Button type="primary" size="small" disabled={!name} onClick={addItem}>
          {getText(
            'data_manager.study_combinations_mapping.add_and_use_own_input'
          )}
        </Button>
      </Space>
      <Divider style={{ margin: '8px 0' }} />
      {props.menu}
    </>
  );
};

export const StudyCombinationsMappingTable = () => {
  const dispatch = useAppDispatch();

  const isLoadingPathwayConfigs = useAppSelector(
    isLoadingPathwayConfigsSelector
  );
  const isLoadingObjectTypes = useAppSelector(selectObjectTypesLoading);
  const isLoadingFields = useAppSelector(fieldsLoading);
  const isLoading = useMemo(
    () => isLoadingPathwayConfigs || isLoadingObjectTypes || isLoadingFields,
    [isLoadingPathwayConfigs, isLoadingObjectTypes, isLoadingFields]
  );

  const pathwayConfigs = useAppSelector(pathwayConfigsSelector);
  const mappingChanges = useAppSelector(mappingChangesSelector);
  const objectTypes = useAppSelector(selectObjectTypesList);
  const fields = useAppSelector(fieldsSelector);

  useEffect(() => {
    // Fetch all data needed for the table
    dispatch(fetchPathwayConfigs());
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
  }, [dispatch]);

  const getChildren = (
    parent: TType,
    allObjectTypes: TType[]
  ): TreeSelectInput['options'] => {
    // Recursively get the children of the parent object type
    const subTypes = parent?.subTypes?.map((subType) =>
      allObjectTypes.find((objectType) => objectType.id === subType)
    );

    return (
      subTypes?.map((subType) => ({
        title: subType!.name,
        value: subType!.extId,
        children: getChildren(subType, allObjectTypes),
      })) ?? []
    );
  };

  const objectTypeOptions: TreeSelectInput['options'] = useMemo(() => {
    // Create a tree structure of the object types
    const parentFields = objectTypes.filter(
      (objectType) =>
        objectType.parentFields == null || objectType.parentFields?.length === 0
    );
    return parentFields.map((parentField) => ({
      title: parentField.name,
      value: parentField.extId,
      children: getChildren(parentField, objectTypes),
    }));
  }, [objectTypes]);

  const [fieldOptionsFromFreeUserInput, setFieldOptionsFromUserFreeInput] =
    useState<{ label: string; value: string }[]>([]);

  const fieldOptions: SelectInput['options'] = useMemo(() => {
    return [
      ...fieldOptionsFromFreeUserInput,
      ...fields.map((field) => ({
        label: `${field.name} (${field.extId})`,
        value: field.extId,
      })),
    ];
  }, [fields, fieldOptionsFromFreeUserInput]);

  const fieldOptionsAdd = (valueToAdd: string) => {
    if (
      !fieldOptionsFromFreeUserInput.some((field) => field.value === valueToAdd)
    ) {
      setFieldOptionsFromUserFreeInput((prev) => [
        { label: valueToAdd, value: valueToAdd },
        ...prev,
      ]);
    }
  };

  useEffect(() => {
    if (isLoading === false) {
      const fieldMappingsInStoredConfigUnique = [
        ...new Set(
          pathwayConfigs
            .flatMap((period) => [
              period?.activityValuesMapping?.numberOfStudentsExtId,
              period?.activityValuesMapping?.trackCapacityExtId,
            ])
            .filter((field) => field)
        ),
      ];
      const fieldMappingsInStoredConfigNotInFieldOptions =
        fieldMappingsInStoredConfigUnique.filter(
          (fieldStored) =>
            !fieldOptions.some(
              (fieldOption) => fieldOption.value === fieldStored
            )
        );

      for (const field of fieldMappingsInStoredConfigNotInFieldOptions) {
        fieldOptionsAdd(field);
      }
    }
  }, [isLoading]);

  const storedMappings: TMappings = useMemo(
    () =>
      Object.fromEntries(
        pathwayConfigs.map((period) => [
          period._id.toString(),
          {
            primaryObject: period.activityValuesMapping?.primaryObjectExtId,
            activityType: period.activityValuesMapping?.activityTypeExtId,
            expectedEnrolment:
              period.activityValuesMapping?.numberOfStudentsExtId,
            trackMaxCapacity: period.activityValuesMapping?.trackCapacityExtId,
          },
        ])
      ),
    [pathwayConfigs]
  );

  const columns: TColumns<TColumn, TTableData> = useMemo(
    () => ({
      period: {
        label: getText(
          'data_manager.study_combinations_mapping.page.table.column.period'
        ),
        key: 'period',
        resizable: true,
        sortDirections: ['descend', 'ascend'],
        sorter: (a, b) => a.period.localeCompare(b.period),
      },
      primaryObject: {
        label: getText(
          'data_manager.study_combinations_mapping.page.table.column.primary_object'
        ),
        key: 'primaryObject',
        resizable: true,
        input: {
          type: 'tree-select',
          options: objectTypeOptions,
          style: { width: '100%' },
        },
      },
      activityType: {
        label: getText(
          'data_manager.study_combinations_mapping.page.table.column.activity_type'
        ),
        key: 'activityType',
        resizable: true,
        input: {
          type: 'tree-select',
          options: objectTypeOptions,
          style: { width: '100%' },
        },
      },
      expectedEnrolment: {
        label: getText(
          'data_manager.study_combinations_mapping.page.table.column.expected_enrolment'
        ),
        key: 'expectedEnrolment',
        resizable: true,
        input: {
          type: 'select',
          options: fieldOptions,
          style: { width: '100%' },
          popupRenderIncludingChangeCallback:
            (changeValueOfSelectFromPopup: (valueToSetTo: string) => void) =>
            (menu) => (
              <SelectPopupComp
                menu={menu}
                fieldOptionsAdd={fieldOptionsAdd}
                changeValueOfSelectFromPopup={changeValueOfSelectFromPopup}
              />
            ),
        },
      },
      trackMaxCapacity: {
        label: getText(
          'data_manager.study_combinations_mapping.page.table.column.track_max_capacity'
        ),
        key: 'trackMaxCapacity',
        resizable: true,
        input: {
          type: 'select',
          options: fieldOptions,
          style: { width: '100%' },
          popupRenderIncludingChangeCallback:
            (changeValueOfSelectFromPopup: (valueToSetTo: string) => void) =>
            (menu) => (
              <SelectPopupComp
                menu={menu}
                fieldOptionsAdd={fieldOptionsAdd}
                changeValueOfSelectFromPopup={changeValueOfSelectFromPopup}
              />
            ),
        },
      },
    }),
    [objectTypeOptions, fieldOptions]
  );

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    useSortColumnsModal<TColumn, TTableData>(columns);

  const tableData: TTableData[] = useMemo(
    () =>
      pathwayConfigs.map((period, index) => {
        const getFormattedDate = (date: Date) => {
          return moment(date).format('YYYY-MM-DD');
        };

        const changedMapping = mappingChanges[period._id.toString()];
        const originalMapping = period.activityValuesMapping;

        return {
          ...{
            key: period._id.toString(),
            period: `${getFormattedDate(
              period.period.startDate
            )} - ${getFormattedDate(period.period.endDate)} (${period.name})`,
            primaryObject: originalMapping?.primaryObjectExtId,
            activityType: originalMapping?.activityTypeExtId,
            expectedEnrolment: originalMapping?.numberOfStudentsExtId,
            trackMaxCapacity: originalMapping?.trackCapacityExtId,
          },
          ...changedMapping,
        };
      }),
    [pathwayConfigs, mappingChanges]
  );

  const { token } = useToken();

  const title = useMemo(
    () => (
      <div
        className="te-flex te-justify-between"
        style={{
          padding: '8px',
          width: '100%',
          height: '100%',
          backgroundColor: token.colorBgLayout,
          borderRadius: '6px 6px 0 0',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Typography.Text
          style={{
            color: token.colorTextHeading,
          }}
        >
          {getInlineString(
            'data_manager.study_combinations_mapping.page.table.header.number_of_periods',
            pathwayConfigs.length
          )}
        </Typography.Text>
        <div>
          <Divider type="vertical" />
          <Button size="small" onClick={openSortColumnsModal}>
            {getText('ui.table.edit_columns')}
          </Button>
        </div>
      </div>
    ),
    [
      openSortColumnsModal,
      pathwayConfigs.length,
      token.colorBgLayout,
      token.colorBorderSecondary,
      token.colorTextHeading,
    ]
  );

  const getNewMappings = useCallback(
    (periodId: string, column: TMappingType, newValue?: string) => {
      const oldValue = storedMappings[periodId]?.[column];

      if (newValue === oldValue) {
        // The value is the same as the original value so we can remove the mapping
        const { [column]: _, ...rest } = mappingChanges[periodId] ?? {};

        if (isEmpty(rest)) {
          // Remove the period from the changed data if it has no values
          const { [periodId]: _, ...rest } = mappingChanges;
          return rest;
        }

        return {
          ...mappingChanges,
          [periodId]: rest,
        };
      }

      // Add the value to the mappings
      return {
        ...mappingChanges,
        [periodId]: { ...mappingChanges[periodId], [column]: newValue },
      };
    },
    [storedMappings, mappingChanges]
  );

  const onDataChange = useCallback(
    (periodId: string, column: TMappingType, value?: string) => {
      dispatch(setMappingChanges(getNewMappings(periodId, column, value)));
    },
    [getNewMappings]
  );

  const fetchingPathwayConfigsError = useAppSelector(
    fetchingPathwayConfigsErrorSelector
  );
  const fetchObjectTypesHasError = useAppSelector(
    (state) => state.objectTypes.hasErrors
  );
  const fetchFieldsHasError = useAppSelector((state) => state.fields.hasErrors);

  const errors = useMemo(
    () => [
      {
        hasError: (fetchingPathwayConfigsError?.length ?? 0) > 0,
        isLoading: isLoadingPathwayConfigs,
        message: `${getText(
          'data_manager.study_combinations_mapping.page.error_result.fetch_pathway_configs'
        )}: ${fetchingPathwayConfigsError}`,
        fetchAction: () => dispatch(fetchPathwayConfigs()),
      },
      {
        hasError: fetchObjectTypesHasError,
        isLoading: isLoadingObjectTypes,
        message: `${getText(
          'data_manager.study_combinations_mapping.page.error_result.fetch_object_types'
        )}: ${getText(
          'data_manager.study_combinations_mapping.page.error_result.unknown_error'
        )}`,
        fetchAction: () => dispatch(requestObjectTypes()),
      },
      {
        hasError: fetchFieldsHasError,
        isLoading: isLoadingFields,
        message: `${getText(
          'data_manager.study_combinations_mapping.page.error_result.fetch_fields'
        )}: ${getText(
          'data_manager.study_combinations_mapping.page.error_result.unknown_error'
        )}`,
        fetchAction: () => dispatch(fetchFields()),
      },
    ],
    [
      fetchingPathwayConfigsError,
      fetchObjectTypesHasError,
      fetchFieldsHasError,
      isLoadingPathwayConfigs,
      isLoadingObjectTypes,
      isLoadingFields,
      dispatch,
    ]
  );

  return (
    <ErrorResult errors={errors}>
      {sortColumnsModal}
      <EditableTable
        key={`${objectTypes.length} - ${fields.length}`} // Force re-mount of the table when object types or fields are updated to avoid issues with the tree select inputs
        title={() => title}
        loading={isLoading}
        columns={sortedColumns} // Note: Columns need to contain 'key' for data to be displayed in the table, but causes performance issues when switching between pages in the table
        dataSource={tableData}
        onSelectChange={(columnKey, { key: periodId }, option) => {
          onDataChange(periodId, columnKey as TMappingType, option?.value);
        }}
      />
    </ErrorResult>
  );
};
